import { useEffect, useRef } from 'react';
import type { LatLng } from '../lib/geo';
import { fetchWeather, type WeatherForecast } from '../lib/weather';
import { useAppStore } from './store';

export type WeatherStatus = 'idle' | 'loading' | 'ok' | 'error';

/**
 * Previsioni Open-Meteo per il punto indicato, con cache nello store per
 * posizione (~1 km) e finestra di 30 minuti. Un errore non blocca nulla:
 * il calcolo dell'ombra non dipende dal meteo.
 */
export function useWeather(ll: LatLng | null): {
  forecast: WeatherForecast | null;
  status: WeatherStatus;
} {
  const weather = useAppStore((s) => s.weather);
  const weatherKey = useAppStore((s) => s.weatherKey);
  const setWeather = useAppStore((s) => s.setWeather);
  const llRef = useRef(ll);
  llRef.current = ll;

  const key = ll
    ? `${ll.lat.toFixed(2)},${ll.lng.toFixed(2)}:${Math.floor(Date.now() / (30 * 60_000))}`
    : null;

  useEffect(() => {
    const target = llRef.current;
    if (!key || !target || key === weatherKey) return;
    const ctrl = new AbortController();
    fetchWeather(target, { signal: ctrl.signal, days: 7 })
      .then((f) => {
        if (!ctrl.signal.aborted) setWeather(f, key);
      })
      .catch(() => {
        // memorizza il fallimento per non martellare il servizio a ogni ricalcolo
        if (!ctrl.signal.aborted) setWeather(null, key);
      });
    return () => ctrl.abort();
  }, [key, weatherKey, setWeather]);

  if (!key) return { forecast: null, status: 'idle' };
  if (key !== weatherKey) return { forecast: null, status: 'loading' };
  return weather ? { forecast: weather, status: 'ok' } : { forecast: null, status: 'error' };
}
